<template>
  <div
    v-if="resolvedPlan"
    :class="{
      'mb-6 w-full px-3 md:w-1/2 lg:mb-0 lg:w-1/4': true,
    }"
    :style="`--recommendedImageSrc: url(${recommendedImageSrc})`"
  >
    <div
      :class="{
        'plan-item group relative flex h-full flex-col border border-b-4 border-solid border-www-color-neutral-very-light border-b-www-color-primary-lighter bg-www-color-white shadow-[0_0_20px_0_rgba(0,0,0,0.15)]': true,
        'text-center transition-all duration-300 ease-in-out': true,
        'group cursor-pointer hover:border-www-color-primary-lighter': true,
        'border-www-color-primary-lighter': highlightPlan,
        'hekate-input-selected hekate-input-highlighted': isSelected,
      }"
      @click="emit('select')"
    >
      <div
        :class="{
          'on-hover absolute top-0 -z-[1] w-full bg-www-color-primary-lighter font-ptc-Rajdhani text-base font-bold uppercase text-www-color-white transition-all duration-300 ease-in-out': true,
          'group-hover:top-[-25px]': true,
        }"
      >
        Signup for free
      </div>
      <div
        :class="{
          'plan-card flex-grow p-4': true,
        }"
      >
        <h3
          :class="{
            'mb-4 text-xl font-extrabold text-www-color-neutral-medium-dark group-hover:text-www-color-primary-lighter': true,
            'text-www-color-primary-lighter': highlightPlan,
          }"
        >
          {{ resolvedPlan.name }}
        </h3>
        <p
          :class="{
            'text-10px font-bold leading-5 text-www-color-neutral-medium-light': true,
          }"
        >
          {{ resolvedPlan.tagline }}
        </p>
        <div
          :class="{
            'plan-line relative -mx-4 mb-9 mt-5 h-1px bg-www-color-neutral-very-light': true,
          }"
        ></div>
        <div
          :class="{
            'mb-4 h-70px': true,
            hidden: !showPrice,
          }"
        >
          <span
            :class="{
              'block min-h-3 text-center text-10px font-bold text-www-color-neutral-light': true,
              hidden: !showPrice,
            }"
            >{{ resolvedPlan.priceDescription }}</span
          >
        </div>

        <div
          :class="{
            'mx-[-1rem] mb-9 mt-5 h-1px bg-www-color-neutral-very-light': true,
            hidden: !showPrice,
          }"
        ></div>
        <div
          ref="featureList"
          :class="{
            'features-list': true,
            open: isFeaturesOpen,
          }"
          :style="isFeaturesOpen ? { maxHeight: height + 'px' } : { maxHeight: '0px' }"
        >
          <PortableText
            v-if="resolvedPlan.summaryList?.standardPortableText"
            :portable-data="resolvedPlan.summaryList?.standardPortableText"
            :class="{
              'g-0': true,
            }"
          />
        </div>
        <h2
          :class="{
            'accordion-header block lg:hidden': true,
          }"
        >
          <button
            :class="{
              'pt-features-mobile collapsed toggle-features-plan-card': true,
            }"
            type="button"
            :aria-expanded="isFeaturesOpen"
            @click.stop="emit('toggleFeatures')"
          >
            <span
              :class="{
                'show-features cursor-pointer font-sans text-sm font-medium leading-9 text-www-color-accent-teal-soft': true,
                hidden: isFeaturesOpen,
              }"
              >{{ resolvedPlan.showFeatures
              }}<span
                :class="{
                  '-mb-1 ml-6px inline-flex h-5 w-5 items-center justify-center rounded-full border': true,
                }"
                >+</span
              ></span
            >
            <span
              :class="{
                'hide-features cursor-pointer font-sans text-sm font-medium leading-9 text-www-color-accent-teal-soft': true,
                hidden: !isFeaturesOpen,
              }"
              >{{ resolvedPlan.hideFeatures
              }}<span
                :class="{
                  '-mb-1 ml-6px inline-flex h-5 w-5 items-center justify-center rounded-full border': true,
                }"
                >-</span
              ></span
            >
          </button>
        </h2>
        <div
          v-if="recommended"
          :class="{
            'mx-auto mb-5 mt-10 w-[80%] rounded-[2px] bg-www-color-primary-lighter py-5px font-ptc-Rajdhani text-base font-semibold uppercase leading-7 text-www-color-black-gradient': true,
          }"
        >
          <span
            :class="{
              'relative flex items-center justify-center gap-2': true,
              'recommended text-www-color-white': true,
            }"
          >
            <span
              class="block h-[18px] w-[16px] bg-no-repeat"
              :style="`background-image: url(${recommendedImageSrc})`"
              aria-hidden="true"
            ></span>
            recommended
          </span>
        </div>
      </div>
      <PortableText
        v-if="
          resolvedPlan.highlight?.standardPortableText &&
          resolvedPlan.highlight?.standardPortableText.length > 0
        "
        :portable-data="resolvedPlan.highlight?.standardPortableText"
        :class="{
          'g-0 mb-0 bg-www-color-accent-blue-soft p-4 text-center text-xs font-extrabold italic leading-5 text-www-color-neutral-medium-dark': true,
        }"
      />
      <div
        :class="{
          'description relative h-24 bg-www-color-neutral-off-white px-4 py-4 text-xs font-medium leading-5 text-www-color-neutral-medium-dark': true,
          'border-t border-www-color-neutral-very-light': true,
        }"
      >
        {{ resolvedPlan.description }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import PortableText from '~/components/Atoms/PortableText/PortableText.vue';
import type { UssPlanSelectionCard, PlanRefQueryResult } from '~/sanity/types';

interface Props extends UssPlanSelectionCard {
  resolvedPlan?: PlanRefQueryResult;
  isSelected?: boolean;
  isFeaturesOpen?: boolean;
}

withDefaults(defineProps<Props>(), {
  isSelected: false,
  isFeaturesOpen: true,
  resolvedPlan: undefined,
});

const emit = defineEmits<{
  select: [];
  toggleFeatures: [];
}>();

const featureList = ref<HTMLElement | null>(null);
const height = ref(0);
watch(
  featureList,
  () => {
    if (featureList.value) {
      height.value = featureList.value.scrollHeight;
    }
  },
  { immediate: true }
);
const { $staticImages } = useNuxtApp();
const recommendedImageSrc = ref($staticImages['recommended']);
</script>
<style>
.recommended:before {
  background-image: var(--recommendedImageSrc);
}
</style>
